import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Share2, Clock, Mail, Briefcase, ChevronDown, ChevronUp } from 'lucide-react';
import { useSharedAccess } from './hooks/useSharedAccess';
import { ShareExpandedDetail } from './share/ShareExpandedDetail';

const ROLE_LABELS = {
  accountant: 'Accountant', tax_advisor: 'Tax Advisor', family_office: 'Family Office Manager',
  estate_planner: 'Estate Planner', other: 'Other',
};

const SCOPE_LABELS = {
  full: 'Full Portfolio', direct_only: 'Direct Only', indirect_only: 'Indirect Only',
  asset_classes: 'Specific Asset Classes', entities: 'Specific Entities',
};

const STATUS_STYLES = {
  active: 'bg-emerald-100 text-emerald-800', expired: 'bg-slate-100 text-slate-600', revoked: 'bg-red-100 text-red-700',
};

const SharedAccessDashboard = () => {
  const {
    shares, loading, expandedShare, activityLogs, extendDays, setExtendDays,
    revokeConfirm, setRevokeConfirm, toggleExpand, handleExtend, handleRevoke,
    formatDate, getShareUrl,
  } = useSharedAccess();

  if (loading) {
    return <Card><CardContent className="p-8 text-center text-slate-500">Loading shared access data...</CardContent></Card>;
  }

  if (shares.length === 0) {
    return (
      <Card><CardContent className="p-8 text-center">
        <Share2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 text-sm">No portfolio shares yet. Use "Share Portfolio View" to grant access to a professional.</p>
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="shared-access-dashboard">
      {shares.map(share => (
        <Card key={share.share_id} className="overflow-hidden">
          <div className="p-4 flex items-center gap-4 cursor-pointer hover:bg-slate-50 transition-colors" onClick={() => toggleExpand(share.share_id)} data-testid={`share-row-${share.share_id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-slate-900 truncate">{share.recipient_name}</span>
                <Badge className={`text-xs ${STATUS_STYLES[share.status] || STATUS_STYLES.expired}`}>{share.status}</Badge>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{share.recipient_email}</span>
                <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{ROLE_LABELS[share.recipient_role] || share.recipient_role}</span>
              </div>
            </div>
            <div className="text-right text-xs text-slate-500 hidden sm:block">
              <p>Scope: {SCOPE_LABELS[share.scope] || share.scope}</p>
              <p className="flex items-center gap-1 justify-end"><Clock className="w-3 h-3" />Expires: {formatDate(share.expires_at)}</p>
            </div>
            {expandedShare === share.share_id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
          {expandedShare === share.share_id && (
            <ShareExpandedDetail share={share} activityLogs={activityLogs} extendDays={extendDays} setExtendDays={setExtendDays}
              revokeConfirm={revokeConfirm} setRevokeConfirm={setRevokeConfirm} handleExtend={handleExtend} handleRevoke={handleRevoke}
              formatDate={formatDate} getShareUrl={getShareUrl} />
          )}
        </Card>
      ))}
    </div>
  );
};

export default SharedAccessDashboard;
